/**
 * Formats a phone number to E.164 format
 * E.164 format: +[country code][number]
 *
 * @param phone - The phone number to format
 * @param defaultCountryCode - Default country code to use if not present (default: 966 for Saudi Arabia)
 * @returns Formatted phone number in E.164 format (e.g., +966501234567)
 */
export function formatPhoneToE164(
  phone: string,
  defaultCountryCode: string = "966"
): string {
  if (!phone) {
    return phone;
  }

  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, "");

  // If already in E.164 format (starts with +), return as is after cleaning
  if (cleaned.startsWith("+")) {
    return cleaned;
  }

  // If starts with 00 (international format without +), replace with +
  if (cleaned.startsWith("00")) {
    return "+" + cleaned.substring(2);
  }

  // Remove leading zeros
  let digits = cleaned.replace(/^0+/, "");

  // If the number already starts with the default country code (after removing leading zeros),
  // it likely already has the country code, just add +
  if (digits.startsWith(defaultCountryCode)) {
    return "+" + digits;
  }

  // For numbers with 12+ digits (after removing leading zeros), they likely already have a country code
  // Add + prefix and return (assumes it's already properly formatted)
  if (digits.length >= 12) {
    return "+" + digits;
  }

  // For local numbers (typically 9 digits for Saudi Arabia), add country code
  // Remove any remaining leading zeros
  const localNumber = digits.replace(/^0+/, "");

  // Validate local number has at least 9 digits (Saudi mobile numbers are 9 digits)
  if (localNumber.length < 9) {
    // If too short, still format it but it might be invalid
    return "+" + defaultCountryCode + localNumber;
  }

  // Add country code and format as E.164
  return "+" + defaultCountryCode + localNumber;
}
